import * as React from 'react';
import { createContext, use } from 'react';
import { useCallback, useRef, useState } from 'react';
import { reducer, initialState, Action, State } from 'store';
import { IContext, ThunkAction, ThunkDispatch } from './AppProviderModels';

export const AppContext = createContext<IContext>({
    state: { ...initialState },
    dispatch: () => initialState,
});

export const useAppContext = () => use(AppContext);

interface IAppProviderProps {
    children?: React.ReactNode;
}

export const AppProvider: React.FC<IAppProviderProps> = (props) => {
    const { children } = props;
    const [state, dispatch] = useThunkReducer(reducer, initialState);
    const context = {
        state,
        dispatch,
    };

    return <AppContext value={context}>
        {children}
    </AppContext>;
};

const useThunkReducer = (reducer: (state: State, action: Action) => State, initialArg: State): [State, ThunkDispatch] => {
    const [hookState, setHookState] = useState(initialArg);

    // State management.
    const stateRef = useRef(hookState);
    const setState = useCallback<(newState: State) => void>((newState) => {
        stateRef.current = newState;
        setHookState(newState);
    }, [stateRef, setHookState]);

    // Reducer.
    const reduce = useCallback<(action: Action) => State>((action) => {
        return reducer(stateRef.current, action);
    }, [reducer]);

    // Augmented dispatcher.
    const dispatch: ThunkDispatch = useCallback((action: Action | ThunkAction) => {
        return typeof action === 'function'
            ? action(dispatch, () => stateRef.current)
            : setState(reduce(action));
    }, [setState, reduce]);

    return [hookState, dispatch];
};
